import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SearchBox, Spinner, SpinnerSize } from '@fluentui/react';
import ReactECharts from 'echarts-for-react';
import {
    BriefcaseRegular,
    BuildingRegular,
    CalendarCheckmarkRegular,
    ClipboardTaskListLtrRegular,
    PeopleRegular,
    TargetArrowRegular
} from '@fluentui/react-icons';

import { AppState } from '../../../redux/reducers';
import * as MedicalProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';
import * as MedicalProtocolsService from '../../../services/MedicalProtocols.Services';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import {
    ProtocolPatientStatusDetail,
    ProtocolPatientStatusStatistics
} from '../../../models/Entities/Protocols/ProtocolPatientStatusStatistics';

import PatientStatusStatisticsComponent from '../../Contracts/statistics/PatientStatusStatistics.Component';
import ExcelExportButton from '../../Contracts/statistics/ExcelExportButton.Component';
import { buildFileName, downloadWorkbook } from '../../Contracts/statistics/StatisticsExcel.Functions';
import { formatMonth, VisitTimelinePoint } from '../../Contracts/statistics/VisitExecution.Functions';
import * as Functions from './ProtocolsPortfolioStatistics.Functions';
import * as Excel from './ProtocolsPortfolioStatistics.Excel';

import '../../Contracts/statistics/ContractStatistics.Styles.css';
import '../../Contracts/statistics/VisitExecution.Styles.css';
import './ProtocolsPortfolioStatistics.Styles.css';

interface ConnectedProps {
    Protocols: Protocol[];
    IsLoadingProtocols: boolean;
    AccessToken: string | undefined;
}

interface DispatchProps {
    GetAllMedicalProtocols: typeof MedicalProtocolStore.actionCreators.GetAllMedicalProtocols;
}

type Props = ConnectedProps & DispatchProps;

/** Pedidos de visitas en simultáneo: suficientes para que no tarde, pocos para no saturar la API. */
const VISITS_BATCH = 4;

/** Alcance que se escribe dentro de cada Excel de la cartera. */
const PORTFOLIO_SCOPE = 'Protocolos ongoing';

/** Cuántas categorías se dibujan antes de agrupar el resto: más, y las barras no se leen. */
const MAX_BARS = 10;

const limitItems = (items: Functions.CountItem[]): Functions.CountItem[] => {
    if (items.length <= MAX_BARS) return items;
    const rest = items.slice(MAX_BARS - 1).reduce((acc, item) => acc + item.count, 0);
    return [...items.slice(0, MAX_BARS - 1), { key: '__rest__', label: 'Otros', count: rest }];
};

/** Barras horizontales de una distribución, en el azul del tablero. */
const distributionOption = (items: Functions.CountItem[], total: number) => {
    const ordered = items.slice().reverse();
    return {
        grid: { left: 12, right: 56, top: 8, bottom: 8, containLabel: true },
        tooltip: {
            trigger: 'item',
            formatter: (params: any) =>
                `${params.name}<br/><b>${params.value}</b> protocolos · ${total > 0 ? Math.round((params.value / total) * 100) : 0}%`
        },
        xAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#f0efee' } } },
        yAxis: {
            type: 'category',
            data: ordered.map((item) => item.label),
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#c8c6c4' } },
            axisLabel: { width: 160, overflow: 'truncate' }
        },
        series: [
            {
                type: 'bar',
                barMaxWidth: 22,
                itemStyle: { color: '#0078d4', borderRadius: [0, 4, 4, 0] },
                data: ordered.map((item) => item.count),
                label: { show: true, position: 'right', color: '#605e5c', fontSize: 11 }
            }
        ]
    };
};

/** Línea de tiempo mensual: barras por mes y línea del acumulado, igual que en el protocolo. */
const timelineOption = (points: VisitTimelinePoint[], barsLabel: string) => ({
    tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) => {
            const point = points[params[0].dataIndex];
            return `<b>${formatMonth(point.month)}</b><br/>${barsLabel}: <b>${point.count}</b><br/>Acumulado: <b>${point.cumulative}</b>`;
        }
    },
    legend: { data: [barsLabel, 'Acumulado'], bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 12, right: 12, top: 16, bottom: 36, containLabel: true },
    xAxis: { type: 'category', data: points.map((point) => formatMonth(point.month)), axisLabel: { fontSize: 11 } },
    yAxis: [
        { type: 'value', minInterval: 1, name: 'Mes', nameTextStyle: { fontSize: 10 }, splitLine: { lineStyle: { color: '#f0efee' } } },
        { type: 'value', minInterval: 1, name: 'Acumulado', nameTextStyle: { fontSize: 10 }, splitLine: { show: false } }
    ],
    series: [
        {
            name: barsLabel,
            type: 'bar',
            barMaxWidth: 28,
            itemStyle: { color: '#0078d4', borderRadius: [4, 4, 0, 0] },
            data: points.map((point) => point.count)
        },
        {
            name: 'Acumulado',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            symbolSize: 6,
            itemStyle: { color: '#107c10' },
            data: points.map((point) => point.cumulative)
        }
    ]
});

/** Mes con más movimiento de una línea de tiempo, para el indicador. */
const busiestMonth = (points: VisitTimelinePoint[]): VisitTimelinePoint | undefined =>
    points.reduce<VisitTimelinePoint | undefined>((best, point) => (!best || point.count > best.count ? point : best), undefined);

/**
 * Estadísticas de toda la cartera de protocolos.
 *
 * Mismo sistema visual que el tablero de un protocolo. Los datos salen de: la lista
 * de protocolos (la misma del listado), las estadísticas y el detalle de pacientes
 * de todos los protocolos —un pedido cada uno— y, para la línea de tiempo de
 * visitas, las visitas de cada protocolo con pacientes, pedidas en tandas porque la
 * API no las entrega juntas.
 */
const ProtocolsPortfolioStatisticsComponent: React.FC<Props> = (props) => {
    const { Protocols, IsLoadingProtocols, AccessToken } = props;
    const navigate = useNavigate();

    const [statistics, setStatistics] = useState<ProtocolPatientStatusStatistics[] | undefined>(undefined);
    const [isLoadingStatistics, setIsLoadingStatistics] = useState<boolean>(false);
    const [failedStatistics, setFailedStatistics] = useState<boolean>(false);

    const [details, setDetails] = useState<ProtocolPatientStatusDetail[] | undefined>(undefined);
    const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);
    const [failedDetails, setFailedDetails] = useState<boolean>(false);

    const [visitsByProtocol, setVisitsByProtocol] = useState<Functions.ProtocolVisits[]>([]);
    const [visitsLoad, setVisitsLoad] = useState<{ done: number; total: number; failed: number }>({ done: 0, total: 0, failed: 0 });

    const [searchText, setSearchText] = useState<string>('');

    // El tablero mira sólo los protocolos ongoing: los cerrados o en otros estados
    // no aportan a la operación y multiplicaban los pedidos de visitas.
    const ongoing = useMemo(() => Protocols.filter(Functions.isOngoingProtocol), [Protocols]);
    const ongoingIds = useMemo(() => ongoing.map((protocol) => protocol.id), [ongoing]);
    const ongoingKey = ongoingIds.join(',');

    useEffect(() => {
        if (Protocols.length === 0) props.GetAllMedicalProtocols();
        // Sólo al entrar: la lista la mantiene el store.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Estadísticas de pacientes de los protocolos ongoing, en un pedido. Sin ids la
    // API devuelve todos, así que sin protocolos ongoing no se pide nada.
    useEffect(() => {
        if (!AccessToken || !ongoingKey) {
            setStatistics(ongoingKey || Protocols.length === 0 ? undefined : []);
            return;
        }
        let cancelled = false;
        const ids = new Set(ongoingIds);
        setIsLoadingStatistics(true);
        setFailedStatistics(false);

        MedicalProtocolsService.GetProtocolPatientStatusStatistics(ongoingIds, AccessToken)
            .then((response) => {
                if (!cancelled) setStatistics((response ?? []).filter((item) => ids.has(item.protocolId)));
            })
            .catch(() => {
                if (!cancelled) setFailedStatistics(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingStatistics(false);
            });

        return () => {
            cancelled = true;
        };
        // ongoingKey resume a ongoingIds y Protocols: con ellos como dependencia se pediría en cada render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AccessToken, ongoingKey]);

    // Detalle de pacientes de los protocolos ongoing: fechas de randomización para la línea de tiempo.
    useEffect(() => {
        if (!AccessToken || !ongoingKey) {
            setDetails(ongoingKey || Protocols.length === 0 ? undefined : []);
            return;
        }
        let cancelled = false;
        const ids = new Set(ongoingIds);
        setIsLoadingDetails(true);
        setFailedDetails(false);

        MedicalProtocolsService.GetProtocolPatientStatusDetail(ongoingIds, AccessToken)
            .then((response) => {
                if (!cancelled) setDetails((response ?? []).filter((item) => ids.has(item.protocolId)));
            })
            .catch(() => {
                if (!cancelled) setFailedDetails(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingDetails(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AccessToken, ongoingKey]);

    /**
     * Visitas de cada protocolo con pacientes, en tandas de a VISITS_BATCH. El
     * gráfico se va completando a medida que llegan, y un protocolo que falla no
     * frena a los demás: se cuenta y se avisa.
     */
    useEffect(() => {
        if (!AccessToken || !statistics) return;

        const ids = Array.from(new Set(statistics.filter((item) => (item.total || 0) > 0).map((item) => item.protocolId)));
        let cancelled = false;
        setVisitsByProtocol([]);
        setVisitsLoad({ done: 0, total: ids.length, failed: 0 });

        const run = async () => {
            const collected: Functions.ProtocolVisits[] = [];
            let failed = 0;
            for (let index = 0; index < ids.length; index += VISITS_BATCH) {
                const batch = ids.slice(index, index + VISITS_BATCH);
                const results = await Promise.all(
                    batch.map((protocolId) =>
                        MedicalProtocolsService.GetProtocolPatientVisits(protocolId, AccessToken)
                            .then((visits) => ({ protocolId, visits: visits ?? [] }))
                            .catch(() => {
                                failed += 1;
                                return undefined;
                            })
                    )
                );
                if (cancelled) return;
                results.forEach((result) => {
                    if (result) collected.push(result);
                });
                setVisitsByProtocol([...collected]);
                setVisitsLoad({ done: Math.min(index + batch.length, ids.length), total: ids.length, failed });
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [AccessToken, statistics]);

    const summary = useMemo(() => Functions.buildPortfolioSummary(ongoing, statistics ?? []), [ongoing, statistics]);
    const recruitmentRows = useMemo(() => Functions.buildRecruitmentRows(ongoing, statistics ?? []), [ongoing, statistics]);
    const visibleRows = useMemo(() => Functions.searchRecruitmentRows(recruitmentRows, searchText), [recruitmentRows, searchText]);

    const randomizationTimeline = useMemo(
        () => Functions.buildRandomizationTimeline(details ?? [], ongoing),
        [details, ongoing]
    );
    const visitsTimeline = useMemo(
        () => Functions.buildPortfolioVisitsTimeline(visitsByProtocol, ongoing),
        [visitsByProtocol, ongoing]
    );
    const isLoadingVisits = visitsLoad.done < visitsLoad.total;

    // Sin "por estado": con sólo protocolos ongoing sería una barra única.
    const byPhase = useMemo(
        () => limitItems(Functions.countBy(ongoing, (p) => p.phaseId ?? p.phase?.id, (p) => p.phase?.title)),
        [ongoing]
    );
    const bySpecialty = useMemo(
        // Por descripción y no por id: el catálogo de especialidades tiene entradas
        // repetidas con ids distintos, y agrupar por id las mostraba duplicadas.
        () => limitItems(Functions.countByLabel(ongoing, (p) => p.specialty?.description)),
        [ongoing]
    );
    const bySponsor = useMemo(
        () => limitItems(Functions.countByLabel(ongoing, (p) => Functions.sponsorName(p))),
        [ongoing]
    );
    const byIndication = useMemo(
        () => limitItems(Functions.countByLabel(ongoing, (p) => p.indication?.description)),
        [ongoing]
    );

    if (IsLoadingProtocols && Protocols.length === 0) {
        return (
            <div className="cs-loading">
                <Spinner size={SpinnerSize.medium} />
                <span>Cargando la cartera de protocolos…</span>
            </div>
        );
    }

    if (Protocols.length === 0) {
        return (
            <div className="cs-empty">
                <BriefcaseRegular className="cs-empty__icon" />
                <span>Todavía no hay protocolos cargados.</span>
            </div>
        );
    }

    if (ongoing.length === 0) {
        return (
            <div className="cs-empty">
                <BriefcaseRegular className="cs-empty__icon" />
                <span>No hay protocolos ongoing.</span>
                <span className="cs-empty__hint">El tablero de la cartera sólo considera los protocolos en estado ongoing.</span>
            </div>
        );
    }

    const chartHeight = (items: Functions.CountItem[]) => Math.max(160, items.length * 34);

    const DistributionBlock = (title: string, desc: string, items: Functions.CountItem[]) => (
        <div>
            <div className="cs-cf-chart-title">{title}</div>
            <div className="cs-cf-chart-desc">{desc}</div>
            <ReactECharts
                option={distributionOption(items, summary.protocols)}
                style={{ height: chartHeight(items) }}
                opts={{ renderer: 'svg' }}
            />
        </div>
    );

    /** Tabla de reparto por protocolo de una línea de tiempo. */
    const ByProtocolTable = (countLabel: string, items: Functions.ProtocolCount[]) =>
        items.length === 0 ? null : (
            <div className="cs-block--divided">
                <div className="cs-cf-chart-title">Por protocolo</div>
                <div className="cs-table-wrap pss-detail__table">
                    <table className="cs-table">
                        <thead>
                            <tr>
                                <th>Protocolo</th>
                                <th className="cs-table__amount">{countLabel}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr
                                    key={item.protocolId}
                                    className="cs-table__interactive"
                                    onClick={() => navigate(`/protocolsagreements/${item.protocolId}`)}
                                >
                                    <td>
                                        <button
                                            type="button"
                                            className="pps-link"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                navigate(`/protocolsagreements/${item.protocolId}`);
                                            }}
                                        >
                                            {item.protocolNumber}
                                        </button>
                                    </td>
                                    <td className="cs-table__amount">{item.count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );

    const randomizedDated = randomizationTimeline.timeline.points.reduce((acc, point) => acc + point.count, 0);
    const randomizationPeak = busiestMonth(randomizationTimeline.timeline.points);
    const visitsDated = visitsTimeline.timeline.points.reduce((acc, point) => acc + point.count, 0);
    const visitsPeak = busiestMonth(visitsTimeline.timeline.points);

    return (
        <div className="cs-container">
            {/* ── Hero band ── */}
            <div className="cs-hero">
                <div className="cs-hero__header">
                    <div className="cs-hero__title">Tablero de la cartera de protocolos</div>
                    <div className="cs-hero__subtitle">
                        Protocolos ongoing · {summary.protocols} protocolos · {summary.sponsors} sponsors
                    </div>
                </div>

                <div className="cs-hero__body">
                    <div className="cs-hero__kpi">
                        <div className="cs-hero__kpi-label">Protocolos ongoing</div>
                        <div className="cs-hero__kpi-value">{summary.protocols}</div>
                        <div className="cs-hero__kpi-sub">de {Protocols.length} protocolos cargados</div>
                        <div className="cs-hero__kpi-desc">Los únicos que considera este tablero</div>
                    </div>
                    <div className="cs-hero__kpi">
                        <div className="cs-hero__kpi-label">Pacientes</div>
                        <div className="cs-hero__kpi-value">{isLoadingStatistics ? '…' : summary.patients.total}</div>
                        <div className="cs-hero__kpi-desc">Con estado registrado, en los protocolos ongoing</div>
                    </div>
                    <div className="cs-hero__kpi cs-hero__kpi--liquidado">
                        <div className="cs-hero__kpi-label">Randomizados</div>
                        <div className="cs-hero__kpi-value">{isLoadingStatistics ? '…' : summary.patients.randomized}</div>
                        {summary.enrollment !== null && (
                            <div className="cs-hero__kpi-sub">{summary.enrollment}% del objetivo</div>
                        )}
                        <div className="cs-hero__kpi-desc">Pacientes que entraron al estudio</div>
                    </div>
                    <div className="cs-hero__kpi cs-hero__kpi--pendiente">
                        <div className="cs-hero__kpi-label">En selección</div>
                        <div className="cs-hero__kpi-value">{isLoadingStatistics ? '…' : summary.patients.selection}</div>
                        <div className="cs-hero__kpi-sub">{summary.patients.screenFailure} fallas de selección</div>
                        <div className="cs-hero__kpi-desc">Screening y pre-screening</div>
                    </div>
                </div>

                {summary.objective > 0 && summary.enrollment !== null && (
                    <div className="cs-hero__progress">
                        <div className="cs-hero__progress-bar">
                            <div className="cs-hero__progress-fill" style={{ width: `${Math.min(summary.enrollment, 100)}%` }} />
                        </div>
                        <div className="cs-hero__progress-label">
                            {summary.patients.randomized} de {summary.objective} pacientes objetivo randomizados · {summary.enrollment}%
                        </div>
                    </div>
                )}
            </div>

            <div className="cs-content">
                {failedStatistics && (
                    <div className="cs-cf-error" role="alert">
                        <strong>No se pudieron obtener las estadísticas de pacientes.</strong> Las cifras de pacientes, el
                        reclutamiento y la línea de tiempo de visitas quedan vacíos; la composición de la cartera sí está completa.
                    </div>
                )}

                {/* ── Composición de la cartera ── */}
                <div className="cs-card">
                    <div className="cs-card__header cs-card__header--actions">
                        <div className="cs-card__header-text">
                            <div className="cs-card__title">Composición de la cartera</div>
                            <div className="cs-card__desc">Cómo se reparten los protocolos ongoing por fase, especialidad, sponsor e indicación.</div>
                        </div>
                        <ExcelExportButton
                            onExport={() =>
                                downloadWorkbook(
                                    buildFileName('composicion-de-la-cartera'),
                                    Excel.compositionSheets(ongoing, summary),
                                    { title: 'Composición de la cartera', scope: PORTFOLIO_SCOPE }
                                )
                            }
                        />
                    </div>

                    <div className="cs-kpi-grid">
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><ClipboardTaskListLtrRegular /></div>
                            <div className="cs-kpi-cell__value">{summary.protocols}</div>
                            <div className="cs-kpi-cell__label">Protocolos ongoing</div>
                            <div className="cs-kpi-cell__desc">Los que considera el tablero</div>
                            <div className="cs-kpi-cell__sub">{Protocols.length - summary.protocols} en otros estados, fuera del tablero</div>
                        </div>
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><BuildingRegular /></div>
                            <div className="cs-kpi-cell__value">{summary.sponsors}</div>
                            <div className="cs-kpi-cell__label">Sponsors</div>
                            <div className="cs-kpi-cell__desc">Sponsors distintos con protocolos</div>
                        </div>
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--orange"><TargetArrowRegular /></div>
                            <div className="cs-kpi-cell__value">{summary.objective}</div>
                            <div className="cs-kpi-cell__label">Objetivo de pacientes</div>
                            <div className="cs-kpi-cell__desc">Suma de los objetivos cargados en los protocolos</div>
                        </div>
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--gray"><PeopleRegular /></div>
                            <div className="cs-kpi-cell__value">{summary.protocolsWithoutPatients}</div>
                            <div className="cs-kpi-cell__label">Sin pacientes</div>
                            <div className="cs-kpi-cell__desc">Protocolos todavía sin pacientes con estado</div>
                        </div>
                    </div>

                    <div className="cs-card__body">
                        <div className="cs-two-col">
                            {DistributionBlock('Por fase', 'Protocolos según la fase del estudio.', byPhase)}
                            {DistributionBlock('Por especialidad', 'Protocolos según la especialidad.', bySpecialty)}
                        </div>
                        <div className="cs-two-col cs-block--divided">
                            {DistributionBlock('Por sponsor', 'Protocolos según el sponsor.', bySponsor)}
                            {DistributionBlock('Por indicación', 'Protocolos según la indicación.', byIndication)}
                        </div>
                    </div>
                </div>

                {/* ── Estado de los pacientes de los protocolos ongoing ── */}
                <PatientStatusStatisticsComponent
                    AllProtocols
                    ProtocolIds={ongoingIds}
                    ScopeText="de los protocolos ongoing"
                    AccessToken={AccessToken}
                    PreloadedStatistics={statistics}
                    PreloadedLoading={isLoadingStatistics}
                    PreloadedFailed={failedStatistics}
                    PreloadedDetails={details}
                    FileBase="estado-de-los-pacientes-cartera"
                    ExportScope={PORTFOLIO_SCOPE}
                />

                {/* ── Reclutamiento en el tiempo ── */}
                <div className="cs-card">
                    <div className="cs-card__header cs-card__header--actions">
                        <div className="cs-card__header-text">
                            <div className="cs-card__title">Reclutamiento en el tiempo</div>
                            <div className="cs-card__desc">
                                Pacientes randomizados por mes en toda la cartera, según su fecha de randomización.
                            </div>
                        </div>
                        <ExcelExportButton
                            disabled={isLoadingDetails || failedDetails}
                            onExport={() =>
                                downloadWorkbook(
                                    buildFileName('reclutamiento-en-el-tiempo'),
                                    Excel.randomizationTimelineSheets(randomizationTimeline, details ?? [], ongoing),
                                    {
                                        title: 'Reclutamiento en el tiempo',
                                        scope: PORTFOLIO_SCOPE,
                                        note: 'Los pacientes randomizados sin fecha de randomización no entran en la línea de tiempo.'
                                    }
                                )
                            }
                        />
                    </div>

                    {isLoadingDetails ? (
                        <div className="cs-loading cs-loading--compact">
                            <Spinner size={SpinnerSize.small} />
                            <span>Cargando pacientes…</span>
                        </div>
                    ) : failedDetails ? (
                        <div className="cs-empty">
                            <PeopleRegular className="cs-empty__icon" />
                            <span>No se pudo obtener el detalle de los pacientes.</span>
                            <span className="cs-empty__hint">Volvé a abrir la solapa para reintentar.</span>
                        </div>
                    ) : (
                        <>
                            <div className="cs-kpi-grid">
                                <div className="cs-kpi-cell">
                                    <div className="cs-kpi-cell__icon cs-kpi-cell__icon--green"><PeopleRegular /></div>
                                    <div className="cs-kpi-cell__value">{randomizedDated}</div>
                                    <div className="cs-kpi-cell__label">Randomizaciones con fecha</div>
                                    <div className="cs-kpi-cell__desc">Ubicadas en la línea de tiempo</div>
                                    {randomizationTimeline.timeline.undated > 0 && (
                                        <div className="cs-kpi-cell__sub">{randomizationTimeline.timeline.undated} randomizados sin fecha</div>
                                    )}
                                </div>
                                <div className="cs-kpi-cell">
                                    <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><ClipboardTaskListLtrRegular /></div>
                                    <div className="cs-kpi-cell__value">{randomizationTimeline.byProtocol.length}</div>
                                    <div className="cs-kpi-cell__label">Protocolos con randomizaciones</div>
                                    <div className="cs-kpi-cell__desc">Protocolos con al menos un paciente randomizado</div>
                                </div>
                                <div className="cs-kpi-cell">
                                    <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><CalendarCheckmarkRegular /></div>
                                    <div className="cs-kpi-cell__value">{randomizationPeak ? randomizationPeak.count : '—'}</div>
                                    <div className="cs-kpi-cell__label">Mes con más randomizaciones</div>
                                    <div className="cs-kpi-cell__desc">
                                        {randomizationPeak ? formatMonth(randomizationPeak.month) : 'Sin randomizaciones con fecha'}
                                    </div>
                                </div>
                            </div>
                            <div className="cs-card__body">
                                <div className="cs-cf-chart-title">Línea de tiempo</div>
                                <div className="cs-cf-chart-desc">Barras: randomizaciones del mes. Línea: acumulado.</div>
                                {randomizationTimeline.timeline.points.length > 0 ? (
                                    <ReactECharts
                                        option={timelineOption(randomizationTimeline.timeline.points, 'Randomizaciones del mes')}
                                        style={{ height: 280 }}
                                        opts={{ renderer: 'svg' }}
                                    />
                                ) : (
                                    <div className="cs-empty cs-empty--compact">
                                        <span>Todavía no hay randomizaciones con fecha.</span>
                                    </div>
                                )}
                                {ByProtocolTable('Randomizados', randomizationTimeline.byProtocol)}
                            </div>
                        </>
                    )}
                </div>

                {/* ── Visitas en el tiempo ── */}
                <div className="cs-card">
                    <div className="cs-card__header cs-card__header--actions">
                        <div className="cs-card__header-text">
                            <div className="cs-card__title">Visitas en el tiempo</div>
                            <div className="cs-card__desc">
                                Visitas completadas por mes en toda la cartera, según el registro clínico de cada protocolo. Cuenta
                                las visitas Completadas, Modificadas o Auto completadas.
                            </div>
                        </div>
                        <ExcelExportButton
                            disabled={isLoadingVisits || visitsByProtocol.length === 0}
                            onExport={() =>
                                downloadWorkbook(
                                    buildFileName('visitas-en-el-tiempo'),
                                    Excel.visitsTimelineSheets(visitsTimeline, visitsByProtocol, ongoing),
                                    {
                                        title: 'Visitas en el tiempo',
                                        scope: PORTFOLIO_SCOPE,
                                        note: 'Cuenta las visitas Completadas, Modificadas o Auto completadas del registro clínico.'
                                    }
                                )
                            }
                        />
                    </div>

                    {isLoadingVisits && (
                        <div className="cs-card__toolbar">
                            <div className="pps-load">
                                <Spinner size={SpinnerSize.xSmall} />
                                <span>
                                    Cargando visitas: {visitsLoad.done} de {visitsLoad.total} protocolos
                                </span>
                                <div className="cs-progress__bar pps-load__bar">
                                    <div
                                        className="cs-progress__fill"
                                        style={{ width: `${visitsLoad.total > 0 ? Math.round((visitsLoad.done / visitsLoad.total) * 100) : 0}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="cs-kpi-grid">
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--green"><CalendarCheckmarkRegular /></div>
                            <div className="cs-kpi-cell__value">{visitsDated}</div>
                            <div className="cs-kpi-cell__label">Visitas completadas con fecha</div>
                            <div className="cs-kpi-cell__desc">Ubicadas en la línea de tiempo</div>
                            {visitsTimeline.timeline.undated > 0 && (
                                <div className="cs-kpi-cell__sub">{visitsTimeline.timeline.undated} completadas sin fecha</div>
                            )}
                        </div>
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><ClipboardTaskListLtrRegular /></div>
                            <div className="cs-kpi-cell__value">{visitsTimeline.byProtocol.length}</div>
                            <div className="cs-kpi-cell__label">Protocolos con visitas</div>
                            <div className="cs-kpi-cell__desc">
                                De {visitsLoad.total} protocolos con pacientes
                            </div>
                        </div>
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><CalendarCheckmarkRegular /></div>
                            <div className="cs-kpi-cell__value">{visitsPeak ? visitsPeak.count : '—'}</div>
                            <div className="cs-kpi-cell__label">Mes con más visitas</div>
                            <div className="cs-kpi-cell__desc">{visitsPeak ? formatMonth(visitsPeak.month) : 'Sin visitas con fecha'}</div>
                        </div>
                    </div>

                    <div className="cs-card__body">
                        {visitsLoad.failed > 0 && (
                            <div className="cs-cf-error" role="alert">
                                No se pudieron obtener las visitas de {visitsLoad.failed}{' '}
                                {visitsLoad.failed === 1 ? 'protocolo' : 'protocolos'}: no suman a la línea de tiempo.
                            </div>
                        )}
                        <div className="cs-cf-chart-title">Línea de tiempo</div>
                        <div className="cs-cf-chart-desc">Barras: visitas completadas en el mes. Línea: acumulado.</div>
                        {visitsTimeline.timeline.points.length > 0 ? (
                            <ReactECharts
                                option={timelineOption(visitsTimeline.timeline.points, 'Visitas completadas en el mes')}
                                style={{ height: 280 }}
                                opts={{ renderer: 'svg' }}
                            />
                        ) : (
                            <div className="cs-empty cs-empty--compact">
                                <span>
                                    {isLoadingVisits ? 'Las visitas se están cargando…' : 'Todavía no hay visitas completadas con fecha.'}
                                </span>
                            </div>
                        )}
                        {ByProtocolTable('Visitas completadas', visitsTimeline.byProtocol)}
                    </div>
                </div>

                {/* ── Reclutamiento por protocolo ── */}
                <div className="cs-card">
                    <div className="cs-card__header cs-card__header--actions">
                        <div className="cs-card__header-text">
                            <div className="cs-card__title">Reclutamiento por protocolo</div>
                            <div className="cs-card__desc">
                                Pacientes de cada protocolo por etapa, y avance de los randomizados contra el objetivo. Tocá un
                                protocolo para abrirlo.
                            </div>
                        </div>
                        <ExcelExportButton
                            disabled={isLoadingStatistics}
                            onExport={() =>
                                downloadWorkbook(
                                    buildFileName('reclutamiento-por-protocolo'),
                                    Excel.recruitmentSheets(recruitmentRows),
                                    { title: 'Reclutamiento por protocolo', scope: PORTFOLIO_SCOPE }
                                )
                            }
                        />
                    </div>

                    <div className="cs-card__toolbar">
                        <SearchBox
                            className="pps-search"
                            placeholder="Buscar por número, título o sponsor"
                            underlined={false}
                            value={searchText}
                            onChange={(_, newValue) => setSearchText(newValue || '')}
                            onClear={() => setSearchText('')}
                        />
                        <span className="pss-detail__count">
                            {visibleRows.length} de {recruitmentRows.length} protocolos
                        </span>
                    </div>

                    <div className="cs-card__body">
                        {visibleRows.length === 0 ? (
                            <div className="cs-empty cs-empty--compact">
                                <span>Ningún protocolo coincide con la búsqueda.</span>
                            </div>
                        ) : (
                            <div className="cs-table-wrap pps-table-wrap">
                                <table className="cs-table">
                                    <thead>
                                        <tr>
                                            <th>Protocolo</th>
                                            <th>Sponsor</th>
                                            <th>Estado</th>
                                            <th className="cs-table__amount">Selección</th>
                                            <th className="cs-table__amount">Fallas sel.</th>
                                            <th className="cs-table__amount">Randomizados</th>
                                            <th className="cs-table__amount">Objetivo</th>
                                            <th className="pps-progress-col">Avance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleRows.map((row) => (
                                            <tr
                                                key={row.protocolId}
                                                className={'cs-table__interactive' + (row.isOpen ? '' : ' pps-row--closed')}
                                                onClick={() => navigate(`/protocolsagreements/${row.protocolId}`)}
                                            >
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="pps-link"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            navigate(`/protocolsagreements/${row.protocolId}`);
                                                        }}
                                                    >
                                                        {row.protocolNumber}
                                                    </button>
                                                    {row.title && <div className="pps-title">{row.title}</div>}
                                                </td>
                                                <td>{row.sponsor ?? '—'}</td>
                                                <td>
                                                    <span className={'pps-status' + (row.isOpen ? '' : ' pps-status--closed')}>
                                                        {row.status ?? (row.isOpen ? 'En curso' : 'Cerrado')}
                                                    </span>
                                                </td>
                                                <td className="cs-table__amount">{row.counts.selection}</td>
                                                <td className="cs-table__amount">{row.counts.screenFailure}</td>
                                                <td className="cs-table__amount"><strong>{row.counts.randomized}</strong></td>
                                                <td className="cs-table__amount">{row.objective > 0 ? row.objective : '—'}</td>
                                                <td className="pps-progress-col">
                                                    {row.progress === null ? (
                                                        <span className="pps-muted">Sin objetivo</span>
                                                    ) : (
                                                        <div className="pps-progress" title={`${row.progress}% del objetivo`}>
                                                            <div className="cs-progress__bar">
                                                                <div
                                                                    className="cs-progress__fill"
                                                                    style={{ width: `${Math.min(row.progress, 100)}%` }}
                                                                />
                                                            </div>
                                                            <span className="pps-progress__value">{row.progress}%</span>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: AppState): ConnectedProps => ({
    Protocols: state.MedicalProtocols?.list ?? [],
    IsLoadingProtocols: !!state.MedicalProtocols?.isLoadingAll,
    AccessToken: (state as any)?.oidc?.user?.access_token as string | undefined
});

const mapDispatchToProps = {
    GetAllMedicalProtocols: MedicalProtocolStore.actionCreators.GetAllMedicalProtocols
};

export default connect(mapStateToProps, mapDispatchToProps)(ProtocolsPortfolioStatisticsComponent as any);
